import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { onSnakeDied } from '../net/liveSession';
import type { SnakeDied } from '../net/messages';

/**
 * Top-right kill feed — a fading stack of "Player X neutralized Player Y"
 * entries driven by the server's `snake_died` events.
 *
 * Design choices:
 *
 *   1. Uniform styling. The whole point per the latest product call is
 *      that the player can't tell humans from bots in the arena. The
 *      kill feed is the most-watched piece of in-game UI; if humans
 *      and bots styled differently here, it'd defeat the mystery
 *      across the rest of the world.
 *
 *   2. Auto-expire entries after 6 seconds. We store the timestamp
 *      per entry and a single setInterval prunes the list. Without
 *      this an idle session would grow the feed unbounded.
 *
 *   3. Cap displayed length at 5. Older entries fade out via
 *      framer-motion's AnimatePresence even if they haven't hit TTL
 *      yet, so a burst of 10 kills doesn't push the feed offscreen.
 *
 *   4. Render absent killer (environmental death) as "Y was lost"
 *      without naming a killer. We don't have those yet on the
 *      server, but the fallback keeps the UI safe for future paths.
 */

interface FeedEntry {
  uid: number;
  victim: string;
  victimHue: number;
  killer: string;
  killerHue: number;
  receivedAt: number;
}

const MAX_VISIBLE_DESKTOP = 5;
const TTL_MS_DESKTOP = 6000;
let uidSeq = 0;

/** Reactive mobile detection — re-evaluated on resize so a rotated
 *  device picks up the new bucket without a refresh. */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e: MediaQueryListEvent): void => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

export function KillFeed(): React.ReactElement {
  const isMobile = useIsMobile();
  // Per operator feedback: no UI in the way on mobile — the kill feed
  // entirely disappears on small viewports. The death modal still
  // shows who killed you, which is the information that actually
  // matters for the player. Desktop keeps the live feed.
  if (isMobile) return <></>;
  const maxVisible = MAX_VISIBLE_DESKTOP;
  const ttlMs = TTL_MS_DESKTOP;
  const [entries, setEntries] = useState<FeedEntry[]>([]);
  // Held in a ref so the prune interval doesn't capture stale state.
  const entriesRef = useRef<FeedEntry[]>([]);
  entriesRef.current = entries;

  useEffect(() => {
    const offDied = onSnakeDied((e: SnakeDied) => {
      if (!e.byName) return;
      const entry: FeedEntry = {
        uid: ++uidSeq,
        victim:    e.name      || 'Unknown',
        victimHue: e.hue,
        killer:    e.byName,
        killerHue: e.byHue,
        receivedAt: Date.now(),
      };
      setEntries((prev) => {
        const next = [entry, ...prev];
        if (next.length > maxVisible) next.length = maxVisible;
        return next;
      });
    });

    const prune = window.setInterval(() => {
      const now = Date.now();
      const fresh = entriesRef.current.filter((e) => now - e.receivedAt < ttlMs);
      if (fresh.length !== entriesRef.current.length) setEntries(fresh);
    }, 500);

    return () => {
      offDied();
      clearInterval(prune);
    };
  }, [maxVisible, ttlMs]);

  if (entries.length === 0) return <></>;

  return (
    <div
      // Desktop: top-right under the persistent leaderboard panel.
      // Mobile: lifted under the compact rank chip so the kill feed
      // doesn't eat the player's sightline. Also right-aligned with a
      // narrower max-width and a smaller font (see entry below).
      className="pointer-events-none absolute top-[260px] right-3 z-30 flex flex-col gap-1.5 items-end max-w-[300px]"
      role="status"
      aria-live="polite"
      aria-label="Kill feed"
    >
      <AnimatePresence initial={false}>
        {entries.map((e) => (
          <motion.div
            key={e.uid}
            layout
            initial={{ opacity: 0, x: 32, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 24, transition: { duration: 0.25 } }}
            transition={{ type: 'spring', stiffness: 380, damping: 28 }}
            className="hud-panel px-3 py-1.5 text-[11px] font-mono tracking-tight whitespace-nowrap"
          >
            <span
              style={{ color: `hsl(${e.killerHue} 100% 75%)` }}
              className="font-semibold"
            >
              {e.killer}
            </span>
            <span className="text-white/40 mx-1.5">▸</span>
            <span
              style={{ color: `hsl(${e.victimHue} 100% 75%)` }}
              className="font-semibold line-through decoration-white/35 decoration-[1.5px]"
            >
              {e.victim}
            </span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
